<template>
  <!-- Loading: skeleton rows that echo a real table layout. -->
  <div v-if="variant === 'loading'" class="h-es-skel" role="status" aria-live="polite">
    <span class="h-sr">{{ title || 'Loading…' }}</span>
    <div v-for="row in skeletonRows" :key="row" class="h-es-skel__row">
      <span class="h-sk" style="width: 80px"></span>
      <span class="h-sk" style="flex: 1"></span>
      <span class="h-sk" style="width: 70px"></span>
      <span class="h-sk" style="width: 90px"></span>
    </div>
  </div>

  <!-- first-run / no-results / error -->
  <div v-else :class="['h-es', `h-es--${variant}`]">
    <div :class="['h-es__art', artClass]">
      <svg viewBox="0 0 24 24" v-html="iconPath"></svg>
    </div>
    <h3 class="h-es__title">{{ title }}</h3>
    <p class="h-es__msg" v-if="message">{{ message }}</p>
    <div class="h-es__actions">
      <button
        v-if="variant === 'error'"
        type="button"
        class="h-btn h-btn--primary"
        @click="$emit('retry')"
      >
        <svg viewBox="0 0 24 24" class="h-btn__i"><path d="M23 4v6h-6M1 20v-6h6M3.5 9a9 9 0 0 1 14.9-3.4L23 10M1 14l4.6 4.4A9 9 0 0 0 20.5 15"/></svg>
        {{ retryLabel }}
      </button>
      <!-- first-run / no-results primary + secondary actions via slot -->
      <slot name="actions" />
    </div>
  </div>
</template>

<script>
// Empty states — never dead-ends (DESIGN.md). Four variants:
//   first-run   nothing yet — teach + a primary CTA (via #actions slot)
//   no-results  a filter matched nothing — offer to clear (via #actions slot)
//   error       something failed — shows a Retry button that emits 'retry'
//   loading     skeleton rows matching the real layout
// Matches design/components/empty-states.html.
export default {
  name: 'HermesEmptyState',
  props: {
    variant: {
      type: String,
      default: 'first-run',
      validator: (v) => ['first-run', 'no-results', 'error', 'loading'].includes(v),
    },
    title: { type: String, default: '' },
    message: { type: String, default: '' },
    retryLabel: { type: String, default: 'Retry' },
    skeletonRows: { type: Number, default: 4 },
  },
  emits: ['retry'],
  computed: {
    artClass() {
      if (this.variant === 'error') return 'h-es__art--danger';
      if (this.variant === 'no-results') return 'h-es__art--neutral';
      return '';
    },
    iconPath() {
      switch (this.variant) {
        case 'error':
          return '<path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"/><path d="M12 9v4M12 17h.01"/>';
        case 'no-results':
          return '<circle cx="11" cy="11" r="7"/><path d="M21 21l-4.3-4.3"/>';
        default: // first-run
          return '<path d="M12 5v14M5 12h14"/>';
      }
    },
  },
};
</script>

<style scoped>
.h-sr {
  position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px;
  overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; border: 0;
}
.h-es {
  background: var(--h-bg-card, #fff);
  border: 1px solid var(--h-border, #E2E8F0);
  border-radius: var(--h-radius-lg, 10px);
  box-shadow: var(--h-shadow-card);
  padding: 44px 30px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  font-family: var(--h-font-body, system-ui);
}
.h-es__art {
  width: 64px; height: 64px; border-radius: 16px;
  background: var(--h-accent-light, #E0F2FE);
  display: flex; align-items: center; justify-content: center; margin-bottom: 18px;
}
.h-es__art svg { width: 30px; height: 30px; stroke: var(--h-accent-dark, #0369A1); fill: none; stroke-width: 1.6; }
.h-es__art--neutral { background: var(--h-bg-subtle, #F1F5F9); }
.h-es__art--neutral svg { stroke: var(--h-text-tertiary, #94A3B8); }
.h-es__art--danger { background: var(--h-danger-bg, #FEF2F2); }
.h-es__art--danger svg { stroke: var(--h-danger-text, #B91C1C); }
.h-es__title { margin: 0 0 6px; font-size: 16px; font-weight: 600; color: var(--h-text-primary, #0F172A); }
.h-es__msg { margin: 0 0 20px; font-size: 13.5px; color: var(--h-text-secondary, #64748B); max-width: 44ch; }
.h-es__actions { display: flex; gap: 10px; flex-wrap: wrap; justify-content: center; }

.h-btn {
  font: inherit; font-size: 13.5px; font-weight: 500; padding: 9px 16px;
  border-radius: var(--h-radius, 6px); border: 1px solid transparent; cursor: pointer;
  display: inline-flex; gap: 7px; align-items: center;
}
.h-btn:focus-visible { outline: none; box-shadow: var(--h-focus-ring); }
.h-btn--primary { background: var(--h-accent, #0EA5E9); color: #fff; }
.h-btn--primary:hover { background: var(--h-accent-hover, #0284C7); }
.h-btn__i { width: 15px; height: 15px; stroke: currentColor; fill: none; stroke-width: 2; }

/* skeleton */
.h-es-skel {
  position: relative;
  background: var(--h-bg-card, #fff);
  border: 1px solid var(--h-border, #E2E8F0);
  border-radius: var(--h-radius-lg, 10px);
  box-shadow: var(--h-shadow-card);
  overflow: hidden;
}
.h-es-skel__row { display: flex; padding: 12px 14px; gap: 14px; border-bottom: 1px solid var(--h-border, #E2E8F0); }
.h-es-skel__row:last-child { border-bottom: none; }
.h-sk {
  height: 12px; border-radius: 5px;
  background: linear-gradient(90deg, var(--h-bg-subtle, #F1F5F9) 25%, var(--h-border, #E2E8F0) 37%, var(--h-bg-subtle, #F1F5F9) 63%);
  background-size: 400% 100%;
  animation: h-sh 1.4s ease infinite;
}
@keyframes h-sh { 0% { background-position: 100% 0; } 100% { background-position: -100% 0; } }
</style>
